import React, { useState, useRef, useCallback, useEffect } from 'react';
import { formatInTenantTz } from '../../../lib/datetime';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { useToast } from '../../../hooks/use-toast';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useBusiness } from '../../../contexts/BusinessContext';
import {
  Phone, Send, PhoneOff, PlayCircle, Loader2, MessageSquare,
  Bot, User, AlertCircle, Mic, MicOff, Volume2, VolumeX, Activity
} from 'lucide-react';

// Browser STT — available if supported; no feature flag required for internal QA console
const SpeechRecognitionAPI =
  window.SpeechRecognition || window.webkitSpeechRecognition || null;

const LANG_OPTIONS = [
  { code: 'en-US', label: 'English (US)' },
  { code: 'fr-FR', label: 'Français' },
  { code: 'es-ES', label: 'Español' },
  { code: 'de-DE', label: 'Deutsch' },
  { code: 'ar-SA', label: 'العربية' },
];

// ─── Component ────────────────────────────────────────────────────────────────

const AIVoiceSimulator = () => {
  const { toast } = useToast();
  const { makeAuthenticatedRequest } = useCRMAuth();
  const { currentBusiness } = useBusiness();

  // ── UI State ────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [customerMessage, setCustomerMessage] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [selectedLang, setSelectedLang] = useState('en-US');
  const [audioSource, setAudioSource] = useState(''); // 'google' | 'browser' | ''

  const [callForm, setCallForm] = useState({
    customer_phone: '+1234567890',
    business_id: '',
  });

  // ── Refs (stable across renders — safe inside async callbacks) ──
  const recognitionRef = useRef(null);
  const audioContextRef = useRef(null);
  const currentAudioNodeRef = useRef(null); // AudioBufferSourceNode currently playing
  const convScrollRef = useRef(null);

  // Sync refs — avoids stale closure captures in async/event handlers
  const voiceModeRef = useRef(false);
  const callActiveRef = useRef(false);
  const currentSessionRef = useRef(null);
  const selectedLangRef = useRef('en-US');
  const sendMessageRef = useRef(null); // forward-ref to break circular dep

  useEffect(() => { voiceModeRef.current = voiceMode; }, [voiceMode]);
  useEffect(() => { callActiveRef.current = callActive; }, [callActive]);
  useEffect(() => { currentSessionRef.current = currentSession; }, [currentSession]);
  useEffect(() => { selectedLangRef.current = selectedLang; }, [selectedLang]);

  // Auto-scroll conversation list
  useEffect(() => {
    if (convScrollRef.current) {
      convScrollRef.current.scrollTop = convScrollRef.current.scrollHeight;
    }
  }, [conversationHistory, interimTranscript, loading]);

  // Auto-fill business ID from context
  useEffect(() => {
    const brandId = currentBusiness?.brandId;
    if (brandId) setCallForm(prev => ({ ...prev, business_id: prev.business_id || brandId }));
  }, [currentBusiness?.brandId]);

  // ── AudioContext ────────────────────────────────────────────────
  // Must be created/resumed inside a user gesture handler to satisfy autoplay policy.
  const ensureAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  const stopCurrentAudio = useCallback(() => {
    if (currentAudioNodeRef.current) {
      try { currentAudioNodeRef.current.stop(); } catch (_) {}
      currentAudioNodeRef.current = null;
    }
    // Cancel browser TTS queue
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setIsPlayingAudio(false);
    setAudioSource('');
  }, []);

  // ── STT ─────────────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (_) {}
      recognitionRef.current = null;
    }
    setIsListening(false);
    setInterimTranscript('');
  }, []);

  const startListening = useCallback(() => {
    if (!SpeechRecognitionAPI || !callActiveRef.current) return;

    // Always create fresh instance — SpeechRecognition cannot be reused after stop/abort
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (_) {}
      recognitionRef.current = null;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.lang = selectedLangRef.current;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.continuous = false; // Turn-based: fire once per utterance, more reliable

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event) => {
      const allResults = Array.from(event.results);
      // Show running interim transcript
      const interim = allResults.map(r => r[0].transcript).join('');
      setInterimTranscript(interim);

      const lastResult = event.results[event.results.length - 1];
      if (lastResult.isFinal) {
        const finalText = lastResult[0].transcript.trim();
        setInterimTranscript('');
        if (!finalText) return;
        setCustomerMessage(finalText);
        if (voiceModeRef.current && sendMessageRef.current) {
          // Interrupt any playing audio, then auto-send the transcription
          stopCurrentAudio();
          sendMessageRef.current(finalText);
        }
      }
    };

    recognition.onerror = (event) => {
      setInterimTranscript('');
      if (event.error === 'aborted') return; // intentional — not an error
      setIsListening(false);
      // On no-speech in voice mode: wait briefly then restart listening
      if (event.error === 'no-speech' && voiceModeRef.current && callActiveRef.current) {
        setTimeout(startListening, 700);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript('');
      // Do NOT auto-restart here — startListening is called from audio onended
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (e) {
      console.error('[Voice Simulator] SpeechRecognition.start() failed:', e);
    }
  }, [stopCurrentAudio]);

  // ── TTS / Audio Playback ─────────────────────────────────────────
  const playAIAudio = useCallback(async (aiText, audioBase64) => {
    setIsPlayingAudio(true);

    const onEnded = () => {
      setIsPlayingAudio(false);
      setAudioSource('');
      currentAudioNodeRef.current = null;
      // In voice mode: resume listening after AI finishes speaking
      if (voiceModeRef.current && callActiveRef.current) {
        setTimeout(startListening, 350);
      }
    };

    // Priority 1: Backend-provided TTS (Google Cloud Neural2)
    if (audioBase64 && audioBase64.length > 100) {
      try {
        const ctx = ensureAudioContext();
        // Decode base64 → Uint8Array → ArrayBuffer
        const binary = atob(audioBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        // Decode WAV (LINEAR16, 16kHz) via AudioContext
        const audioBuffer = await ctx.decodeAudioData(bytes.buffer.slice(0));
        const node = ctx.createBufferSource();
        node.buffer = audioBuffer;
        node.connect(ctx.destination);
        currentAudioNodeRef.current = node;
        setAudioSource('google');
        node.onended = onEnded;
        node.start(0);
        return; // success — exit here
      } catch (err) {
        console.warn('[Voice Simulator] Google TTS playback failed, falling back to browser TTS:', err.message);
      }
    }

    // Priority 2: Browser SpeechSynthesis (fallback when Google TTS unavailable/failed)
    if ('speechSynthesis' in window && aiText) {
      window.speechSynthesis.cancel(); // clear any queued utterances
      const utt = new SpeechSynthesisUtterance(aiText);
      utt.lang = selectedLangRef.current;
      utt.rate = 1.0;
      utt.pitch = 1.0;
      setAudioSource('browser');
      utt.onend = onEnded;
      utt.onerror = onEnded;
      window.speechSynthesis.speak(utt);
      return;
    }

    // No audio path available — trigger loop anyway so conversation continues
    setAudioSource('');
    onEnded();
  }, [ensureAudioContext, startListening]);

  // ── Core: Send Message to AI ─────────────────────────────────────
  const sendMessageToAI = useCallback(async (text) => {
    const session = currentSessionRef.current;
    if (!text?.trim() || !session) return;

    setLoading(true);
    setCustomerMessage('');

    try {
      const response = await makeAuthenticatedRequest('/api/v1/ai-voice/process-conversation', {
        method: 'POST',
        body: JSON.stringify({
          session_id: session.session_id,
          customer_text: text,
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      const ts = new Date().toISOString();
      setConversationHistory(prev => [
        ...prev,
        {
          turn_id: `t${Date.now()}_c`,
          timestamp: ts,
          speaker: 'customer',
          text,
          language: data.detected_language,
          intent: data.detected_intent,
          confidence: data.intent_confidence,
        },
        {
          turn_id: `t${Date.now()}_a`,
          timestamp: new Date().toISOString(),
          speaker: 'ai_agent',
          text: data.ai_response,
          language: data.detected_language,
        },
      ]);

      if (data.should_escalate) {
        toast({
          title: '⚠️ Escalation Triggered',
          description: data.escalation_reason || 'AI recommends human handoff.',
        });
      }

      // Play AI audio response (Google TTS → browser TTS fallback)
      await playAIAudio(data.ai_response, data.audio_response_base64);

    } catch (error) {
      console.error('[Voice Simulator] sendMessageToAI error:', error);
      toast({
        title: 'Message Failed',
        description: error.message || 'Could not reach AI. Please retry.',
        variant: 'destructive',
      });
      setIsPlayingAudio(false);
      // Resume listening in voice mode even after error
      if (voiceModeRef.current && callActiveRef.current) {
        setTimeout(startListening, 800);
      }
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, playAIAudio, startListening, toast]);

  // Wire sendMessageToAI into ref so startListening callback can call it
  // without needing to re-create startListening every time sendMessageToAI changes
  useEffect(() => {
    sendMessageRef.current = sendMessageToAI;
  }, [sendMessageToAI]);

  // ── Session Control ──────────────────────────────────────────────
  const handleInitiateCall = async () => {
    ensureAudioContext(); // Unlock AudioContext via this user gesture (required for autoplay)
    setLoading(true);
    try {
      const response = await fetch('/api/v1/ai-voice/simulate-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_phone: callForm.customer_phone,
          business_id: callForm.business_id,
        }),
      });
      if (!response.ok) throw new Error('Failed to start session');
      const data = await response.json();

      setCurrentSession(data.session);
      setConversationHistory(data.session.conversation_history || []);
      setCallActive(true);

      toast({ title: 'AI Session Started', description: `Session reference: ${data.session.session_id?.substring(0, 8)}` });

      // If voice mode was pre-enabled, play greeting and start listening
      if (voiceModeRef.current) {
        const greeting = data.session.conversation_history?.find(t => t.speaker === 'ai_agent');
        if (greeting) {
          await playAIAudio(greeting.text, ''); // greeting has no pre-generated audio
        } else {
          setTimeout(startListening, 500);
        }
      }
    } catch (error) {
      toast({ title: 'Session Failed', description: 'Could not start AI simulator session.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleEndCall = useCallback(() => {
    stopListening();
    stopCurrentAudio();
    setCallActive(false);
    setCurrentSession(null);
    setConversationHistory([]);
    setCustomerMessage('');
    setVoiceMode(false);
    voiceModeRef.current = false;
    toast({ title: 'Session Ended' });
  }, [stopListening, stopCurrentAudio, toast]);

  const handleEscalate = async () => {
    if (!currentSession) return;
    setLoading(true);
    try {
      const response = await makeAuthenticatedRequest('/api/v1/ai-voice/escalate', {
        method: 'POST',
        body: JSON.stringify({
          session_id: currentSession.session_id,
          escalation_reason: 'Manual escalation from QA simulator',
          escalate_to: 'operator',
        }),
      });
      if (!response.ok) throw new Error('Escalation failed');
      const data = await response.json();
      toast({ title: 'Escalated', description: `Ticket: ${data.escalated_to}` });
      handleEndCall();
    } catch {
      toast({ title: 'Escalation Failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // ── Voice Mode Toggle ────────────────────────────────────────────
  const toggleVoiceMode = () => {
    if (!callActive) return;
    ensureAudioContext();
    const next = !voiceMode;
    setVoiceMode(next);
    voiceModeRef.current = next;
    if (next) {
      stopCurrentAudio();
      setTimeout(startListening, 300);
    } else {
      stopListening();
    }
  };

  // Manual mic button (used when voice mode is OFF — one-shot capture)
  const toggleManualMic = () => {
    if (isListening) {
      stopListening();
    } else {
      ensureAudioContext();
      startListening();
    }
  };

  // Manual send button / Enter key
  const handleManualSend = () => {
    if (!customerMessage.trim() || !currentSession || loading) return;
    sendMessageToAI(customerMessage);
  };

  // ── UI Helpers ───────────────────────────────────────────────────
  const formatTimestamp = (ts) =>
    formatInTenantTz(ts, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const hasMicSupport = !!SpeechRecognitionAPI;

  const quickMessages = [
    "Hello, I need help with my account. I can't log in.",
    "Bonjour, j'ai besoin d'aide avec mon compte.",
    "Can you help me reset my password?",
    "I want to speak with a manager.",
    "This is taking too long, connect me to a human.",
    "Thank you, that solved my problem!",
    "I'm still having issues, can someone call me back?",
    "Mi factura parece incorrecta. ¿Pueden ayudarme?",
  ];

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-6">
      <Card className="border-2 border-blue-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 flex-wrap">
            <Bot className="h-6 w-6 text-blue-500" />
            AI Voice Agent Simulator
            <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-300 text-xs font-semibold">
              Enterprise QA Console
            </Badge>
            {isPlayingAudio && (
              <Badge
                variant="secondary"
                className={`text-xs font-semibold animate-pulse ${
                  audioSource === 'google'
                    ? 'bg-green-100 text-green-800 border-green-300'
                    : 'bg-blue-100 text-blue-800 border-blue-300'
                }`}
              >
                <Volume2 className="h-3 w-3 mr-1 inline" />
                {audioSource === 'google' ? 'AI Speaking (Google TTS)' : 'AI Speaking (Browser TTS)'}
              </Badge>
            )}
            {isListening && (
              <Badge variant="secondary" className="bg-red-100 text-red-800 border-red-300 text-xs font-semibold animate-pulse">
                <Mic className="h-3 w-3 mr-1 inline" />
                Listening...
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Business AI validation console — simulate real customer interactions, test multilingual handling,
            escalation logic, and AI accuracy after retraining.
            {!hasMicSupport && (
              <span className="ml-1 text-amber-600 font-medium">
                Voice input requires Chrome or Edge.
              </span>
            )}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* ── Left Panel: Setup & Controls ── */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Session Setup
              </h3>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="sim-phone">Simulated Customer Phone</Label>
                  <Input
                    id="sim-phone"
                    value={callForm.customer_phone}
                    onChange={(e) => setCallForm(f => ({ ...f, customer_phone: e.target.value }))}
                    disabled={callActive}
                  />
                </div>
                <div>
                  <Label htmlFor="sim-biz">Business ID</Label>
                  <Input
                    id="sim-biz"
                    value={callForm.business_id}
                    onChange={(e) => setCallForm(f => ({ ...f, business_id: e.target.value }))}
                    placeholder="Your brand/business ID"
                    disabled={callActive}
                  />
                </div>
                <div>
                  <Label htmlFor="sim-lang">Language (STT + TTS)</Label>
                  <select
                    id="sim-lang"
                    className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                    value={selectedLang}
                    onChange={(e) => setSelectedLang(e.target.value)}
                    disabled={callActive}
                  >
                    {LANG_OPTIONS.map(l => (
                      <option key={l.code} value={l.code}>{l.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {!callActive ? (
                <Button
                  onClick={handleInitiateCall}
                  disabled={loading}
                  className="w-full mt-4 bg-green-600 hover:bg-green-700"
                >
                  {loading
                    ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    : <PlayCircle className="h-4 w-4 mr-2" />}
                  Start AI Session
                </Button>
              ) : (
                <div className="space-y-2 mt-4">
                  {/* Voice Mode Toggle */}
                  {hasMicSupport && (
                    <Button
                      onClick={toggleVoiceMode}
                      variant={voiceMode ? 'default' : 'outline'}
                      className={`w-full ${voiceMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}
                    >
                      <Activity className={`h-4 w-4 mr-2 ${voiceMode ? 'animate-pulse' : ''}`} />
                      {voiceMode ? 'Voice Mode ON — Click to disable' : 'Enable Bidirectional Voice Mode'}
                    </Button>
                  )}
                  <Button onClick={handleEndCall} variant="destructive" className="w-full">
                    <PhoneOff className="h-4 w-4 mr-2" />
                    End Session
                  </Button>
                  <Button onClick={handleEscalate} disabled={loading} variant="outline" className="w-full">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    Force Escalate to Human
                  </Button>
                </div>
              )}

              {/* Active Session Info */}
              {currentSession && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-4 text-xs space-y-1">
                    <p className="font-semibold text-sm mb-2">Active Session</p>
                    {/* Diagnostic technical reference only — shortened, never the full session id. */}
                    <p><span className="font-medium">Session reference:</span> {currentSession.session_id?.substring(0, 8)}</p>
                    <p>
                      <span className="font-medium">Mode:</span>{' '}
                      {voiceMode ? '🎙️ Bidirectional Voice' : '⌨️ Manual / Push-to-talk'}
                    </p>
                    <p>
                      <span className="font-medium">TTS:</span>{' '}
                      {audioSource === 'google' ? '✅ Google Cloud Neural2' : audioSource === 'browser' ? '🔈 Browser fallback' : '—'}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Voice Mode Guidance */}
              {callActive && voiceMode && (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="pt-4 text-xs text-green-800 space-y-1">
                    <p className="font-semibold">Voice Mode Active</p>
                    <p>• Speak naturally — STT captures your voice automatically</p>
                    <p>• AI responds with real audio (Google TTS or browser fallback)</p>
                    <p>• Speaking while AI talks interrupts playback</p>
                    <p>• Listening resumes automatically after each AI response</p>
                    <p>• Use the text box to override or send manual messages</p>
                  </CardContent>
                </Card>
              )}

              {/* Quick Test Messages — manual mode only */}
              {callActive && !voiceMode && (
                <div>
                  <h4 className="font-semibold mb-2 text-sm">Quick Test Messages</h4>
                  <div className="grid grid-cols-1 gap-2 max-h-56 overflow-y-auto">
                    {quickMessages.map((msg, idx) => (
                      <Button
                        key={idx}
                        onClick={() => setCustomerMessage(msg)}
                        variant="outline"
                        size="sm"
                        className="text-left justify-start h-auto py-2"
                      >
                        <MessageSquare className="h-3 w-3 mr-2 flex-shrink-0" />
                        <span className="text-xs">{msg}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Right Panel: Conversation ── */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Conversation
              </h3>

              {/* Conversation History */}
              <div
                ref={convScrollRef}
                className="border rounded-lg p-4 h-96 overflow-y-auto bg-gray-50"
              >
                {conversationHistory.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <p className="text-center text-sm">Start a session to begin.<br />Enable Voice Mode for bidirectional conversation.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {conversationHistory.map((turn, idx) => (
                      <div
                        key={turn.turn_id || idx}
                        className={`p-3 rounded-lg ${
                          turn.speaker === 'ai_agent'
                            ? 'bg-blue-100 border-l-4 border-l-blue-500'
                            : 'bg-green-100 border-l-4 border-l-green-500'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-sm flex items-center gap-1">
                            {turn.speaker === 'ai_agent'
                              ? <><Bot className="h-4 w-4" /> AI Agent</>
                              : <><User className="h-4 w-4" /> You (Simulating Customer)</>}
                          </span>
                          <span className="text-xs text-gray-500">{formatTimestamp(turn.timestamp)}</span>
                        </div>
                        <p className="text-sm">{turn.text}</p>
                        {turn.intent && (
                          <div className="mt-2 text-xs flex items-center gap-2">
                            <Badge variant="outline">{turn.intent}</Badge>
                            <span className="text-gray-500">{Math.round((turn.confidence || 0) * 100)}% confidence</span>
                            {turn.language && <span className="text-gray-400">[{turn.language}]</span>}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Live interim transcript */}
                    {interimTranscript && (
                      <div className="p-3 rounded-lg bg-yellow-50 border-l-4 border-l-yellow-400 opacity-80">
                        <span className="text-xs text-yellow-700 font-medium">Transcribing...</span>
                        <p className="text-sm italic text-gray-600 mt-1">{interimTranscript}</p>
                      </div>
                    )}

                    {/* AI processing indicator */}
                    {loading && (
                      <div className="p-3 rounded-lg bg-blue-50 border-l-4 border-l-blue-300">
                        <span className="text-xs text-blue-600 flex items-center gap-2">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          AI processing response...
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Input Area */}
              {callActive && (
                <div className="space-y-2">
                  <Label htmlFor="sim-msg">
                    {voiceMode ? 'Manual Override (type to send while in voice mode)' : 'Customer Message'}
                  </Label>
                  <Textarea
                    id="sim-msg"
                    value={customerMessage}
                    onChange={(e) => setCustomerMessage(e.target.value)}
                    placeholder={voiceMode ? 'Type to send manually...' : 'Type customer message or use mic...'}
                    rows={2}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleManualSend();
                      }
                    }}
                  />
                  <div className="flex gap-2">
                    {/* Manual mic — only shown when voice mode is OFF */}
                    {hasMicSupport && !voiceMode && (
                      <Button
                        onClick={toggleManualMic}
                        variant={isListening ? 'destructive' : 'outline'}
                        size="sm"
                        className="flex-shrink-0"
                        title={isListening ? 'Stop recording' : 'Record voice input'}
                      >
                        {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                      </Button>
                    )}
                    {/* Stop AI audio */}
                    {isPlayingAudio && (
                      <Button
                        onClick={stopCurrentAudio}
                        variant="outline"
                        size="sm"
                        className="flex-shrink-0 border-red-300 text-red-600 hover:bg-red-50"
                        title="Stop AI audio"
                      >
                        <VolumeX className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      onClick={handleManualSend}
                      disabled={loading || !customerMessage.trim()}
                      className="flex-1"
                    >
                      {loading
                        ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        : <Send className="h-4 w-4 mr-2" />}
                      Send
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIVoiceSimulator;
